import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import type { ParseKeys } from 'i18next';
import { useMemo } from 'react';
import { type UseFormSetValue } from 'react-hook-form';
import { type Filter, type SelectOption, type ViewOptions, ViewOptionsView } from '../../../_types/Car';
import CarListSort from '../../../components/car-list-sort';
import CustomPopover, { usePopover } from '../../../components/custom-popover';
import Iconify from '../../../components/iconify/component';
import { useRootContainerContext } from '../../../context/root-container';
import { useSettingsContext } from '../../../context/settings';
import { type UseCarSearchReturn } from '../../../hooks/use-car-search/types';
import { useResponsive } from '../../../hooks/use-responsive';
import useLocales from '../../../hooks/useLocales';
import checkUndefinedNullOrEmpty from '../../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../../utils/tools/getStyleQuery';
import { type Modules } from '../../_definition/types';

type FahrzeugSucheFilterSortProps = {
    filters: Filter;
    listMode: UseCarSearchReturn['listMode'];
    module: Modules;
    onFilters: UseFormSetValue<Filter>;
    setListMode: UseCarSearchReturn['setListMode'];
};

const viewOptionsArray: (Pick<SelectOption, 'icon'> & { i: ViewOptions['view']; t: ParseKeys<'common'> })[] = [
    { i: ViewOptionsView.Grid, t: 'common:carSearch.gridView' as ParseKeys<'common'>, icon: 'mdi:view-module' },
    { i: ViewOptionsView.List, t: 'common:carSearch.listView' as ParseKeys<'common'>, icon: 'mdi:view-list' },
];

export default function FahrzeugSucheFilterSort(props: FahrzeugSucheFilterSortProps) {
    const { filters, listMode, module, onFilters, setListMode } = props;
    const popoverView = usePopover();
    const rootContainer = useRootContainerContext();
    const settings = useSettingsContext();
    const { translate } = useLocales();
    const sm = useResponsive(rootContainer, 'down', 'md');

    const selectedView = useMemo(() => viewOptionsArray?.find((s) => s?.i === listMode), [listMode]);

    return (
        <Stack direction={{ xs: 'column', sm: 'row-reverse', md: 'row' }} alignItems={{ xs: 'center', md: 'flex-end' }} gap={1} sx={{ width: { xs: 1, md: 'unset' } }}>
            <Stack direction="row" justifyContent="flex-end" gap={1}>
                <Stack className="FahrzeugSucheFilterSort_Container" direction={sm ? 'column' : 'row'} justifyContent="flex-end" gap={1}>
                    <CarListSort filters={filters} module={module} onFilters={onFilters} />
                    {!sm && (
                        <Button
                            disableRipple
                            color="inherit"
                            disabled={settings?.general?.mockup}
                            onClick={popoverView.onOpen}
                            endIcon={<Iconify icon={popoverView.open ? 'solar:alt-arrow-up-line-duotone' : 'solar:alt-arrow-down-line-duotone'} />}
                            sx={{ fontWeight: 'fontWeightSemiBold' }}
                        >
                            <Stack
                                alignItems="center"
                                component="span"
                                direction="row"
                                gap={1}
                                sx={{
                                    [getStyleQuery()]: {
                                        fontWeight: 'fontWeightBold',
                                    },
                                }}
                            >
                                <Iconify icon={selectedView?.icon} />
                                {translate(selectedView?.t)}
                            </Stack>
                        </Button>
                    )}
                </Stack>
            </Stack>

            <CustomPopover open={popoverView.open} onClose={popoverView.onClose}>
                {viewOptionsArray
                    ?.filter((val) => !checkUndefinedNullOrEmpty(val))
                    ?.map((option) => (
                        <MenuItem
                            key={option?.i}
                            selected={option?.i === selectedView?.i}
                            onClick={() => {
                                popoverView.onClose();
                                setListMode(option?.i);
                            }}
                        >
                            <Iconify icon={option?.icon} sx={{ mr: 0.5 }} />
                            {translate(option?.t)}
                        </MenuItem>
                    ))}
            </CustomPopover>
        </Stack>
    );
}
